import { type MouseEvent, useState } from "react";
import { SortDirection, type SortFuncMap, useSorting } from "../../../../common/hooks/useSorting";
import { HeaderCellTitle, TableSortLabel } from "../../../../common/components/Table/TableElements";
import { TCell } from "../../../common/components/TableElements";
import { useTranslation } from "react-i18next";

export interface HeaderCellData {
  title: string;
  subtitle: string;
  sortKind: string | null;
}

export function useOrderListData<T extends object>({
  rows,
  sortHandlers,
  initialSortCode,
  translationPrefix,
}: {
  rows: T[];
  sortHandlers: SortFuncMap<T>;
  initialSortCode: string;
  translationPrefix: string;
}) {
  const { t } = useTranslation();
  const [sortCode, setSortCode] = useState(initialSortCode);
  const [sortDirection, setSortDirection] = useState(SortDirection.Desc);
  const sortedRows = useSorting(rows, sortHandlers, sortCode, sortDirection);

  const createSortHandler = (codeToSet: string) => (e: MouseEvent<unknown>) => {
    if (codeToSet === sortCode) {
      setSortDirection(sortDirection === SortDirection.Asc ? SortDirection.Desc : SortDirection.Asc);
    } else {
      setSortCode(codeToSet);
      setSortDirection(SortDirection.Asc);
    }
  };

  const createTableProps = (boundSortCode: string | null) => {
    if (boundSortCode === null) {
      return {};
    }
    return {
      onClick: createSortHandler(boundSortCode),
      active: boundSortCode === sortCode,
      direction: boundSortCode === sortCode ? sortDirection : SortDirection.Desc,
    };
  };

  const headerT = (key: string) => t(`${translationPrefix}${key}`);
  const testIdSorting = `sorting-${sortCode ? "descending" : "ascending"}`; // ${isSelected ? "sorting-selected" : ""} TODO: Add later

  const renderHeaderCell = (data: HeaderCellData, index: number) => {
    let cell = (
      <HeaderCellTitle
        header={headerT(data.title)}
        subheader={data.subtitle ? headerT(data.subtitle) : undefined}
        data-test-id={testIdSorting}
      />
    );
    if (data.sortKind !== null) {
      cell = (
        <TableSortLabel {...createTableProps(data.sortKind)} data-test-id={data.title}>
          {cell}
        </TableSortLabel>
      );
    }
    return <TCell key={index}>{cell}</TCell>;
  };

  return {
    sortedRows,
    renderHeaderCell,
  };
}
